(() => {
  "use strict";
  const RT =
    window.BienestarRuntime ||
    (window.BienestarRuntime = (() => {
      const defs = new Map();
      const roots = (target, selector) => {
        const found = [];
        if (target?.nodeType !== 1 && target !== document) return found;
        if (target !== document && target.matches?.(selector))
          found.push(target);
        target.querySelectorAll?.(selector).forEach((node) => found.push(node));
        return found;
      };
      const mount = (def, root) => {
        if (def.instances.has(root)) return;
        const cleanups = [];
        const onCleanup = (fn) => {
          if (typeof fn === "function") cleanups.push(fn);
        };
        try {
          onCleanup(def.setup(root, onCleanup));
        } catch (error) {
          console.error("[Bienestar]", def.name, error);
        }
        def.instances.set(root, () => {
          for (let i = cleanups.length - 1; i >= 0; i--)
            try {
              cleanups[i]();
            } catch {}
          def.instances.delete(root);
          def.mounted.delete(root);
        });
        def.mounted.add(root);
      };
      const unmount = (def, target) => {
        for (const root of [...def.mounted])
          if (root === target || target.contains?.(root))
            def.instances.get(root)?.();
      };
      const load = (event) =>
        defs.forEach((def) =>
          roots(event.target, def.selector).forEach((root) => mount(def, root)),
        );
      const unload = (event) =>
        defs.forEach((def) => unmount(def, event.target));
      addEventListener("shopify:section:load", load);
      addEventListener("shopify:section:unload", unload);
      return {
        define(name, selector, setup) {
          const previous = defs.get(name);
          if (previous)
            for (const root of [...previous.mounted])
              previous.instances.get(root)?.();
          const def = {
            name,
            selector,
            setup,
            instances: new WeakMap(),
            mounted: new Set(),
          };
          defs.set(name, def);
          roots(document, selector).forEach((root) => mount(def, root));
          return def;
        },
        snapshot() {
          return Object.fromEntries(
            [...defs].map(([name, def]) => [name, def.mounted.size]),
          );
        },
      };
    })());
  RT.define(
    "experiencias",
    '[data-bienestar-section="experiencias"]',
    (root) => {
      const abort = new AbortController(),
        signal = abort.signal,
        motion = matchMedia("(prefers-reduced-motion: reduce)"),
        cards = [...root.querySelectorAll("[data-ugc-card]")];
      const set = (card, audible) => {
        const video = card.querySelector("video"),
          button = card.querySelector(".ugc-reel-audio");
        if (!video || !button) return;
        video.muted = !audible;
        button.setAttribute("aria-pressed", String(audible));
        button.setAttribute(
          "aria-label",
          `${audible ? root.dataset.audioOffLabel : root.dataset.audioOnLabel}, ${card.dataset.ugcLabel}`,
        );
        const label = button.querySelector("span");
        if (label)
          label.textContent = audible
            ? root.dataset.audioOffLabel
            : root.dataset.audioOnLabel;
      };
      cards.forEach((card) => {
        const video = card.querySelector("video"),
          button = card.querySelector(".ugc-reel-audio");
        if (!video || !button) return;
        set(card, false);
        button.addEventListener(
          "click",
          () => {
            const audible = button.getAttribute("aria-pressed") !== "true";
            cards.forEach((other) => set(other, false));
            if (audible) {
              video.currentTime = 0;
              set(card, true);
              video.play().catch(() => set(card, false));
            } else if (motion.matches) video.pause();
          },
          { signal },
        );
        if (motion.matches) video.pause();
      });
      const observer =
        "IntersectionObserver" in window
          ? new IntersectionObserver(
              (entries) =>
                entries
                  .filter((e) => !e.isIntersecting)
                  .forEach((e) => set(e.target, false)),
              { threshold: 0.25 },
            )
          : null;
      cards.forEach((card) => observer?.observe(card));
      return () => {
        abort.abort();
        observer?.disconnect();
        cards.forEach((card) => {
          set(card, false);
          card.querySelector("video")?.pause();
        });
      };
    },
  );
})();
